import { useTranslation } from "react-i18next"
import { type MerchantData } from "types"
import { useEffect, useMemo, useRef, useState } from "react"
import { useMerchantQuery, useSaveMerchantMutation } from "hooks/query/useMerchantQuery.ts"
import { useCurrentId } from "hooks/useCurrentId.ts"
import { useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { useBrandsQuery } from "hooks/query/useBrandsQuery"
import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Search, X } from "lucide-react"

interface MerchantPageProps {
  add?: boolean
}

interface PlanOption {
  id: string
  name: string
}

interface UserOption {
  username: string
  sub: string
}

interface BrandOption {
  id: string
  name: string
}

export const MerchantPage = ({ add }: MerchantPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const merchantId = useCurrentId()
  const { data: merchantData, isLoading } = useMerchantQuery(merchantId)
  const { data: brands, isLoading: isBrandsLoading } = useBrandsQuery()

  const [plans, setPlans] = useState<PlanOption[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [isUsersLoading, setUsersLoading] = useState(true)
  const [brandSearch, setBrandSearch] = useState("")

  const [merchant, setMerchant] = useState<MerchantData>({
    id: "",
    name: "",
    email: "",
    domain: "",
    planId: "",
    cognitoSub: "",
    description: "",
    brandsIds: [],
  })

  const saveMerchantMutation = useSaveMerchantMutation(merchantId)
  const initialMerchant = useRef<string | null>(null)

  useEffect(() => {
    if (merchantData) {
      setMerchant(merchantData)
      initialMerchant.current = JSON.stringify(merchantData)
    }
  }, [merchantData])

  // For add mode, track once first meaningful edit happens
  useEffect(() => {
    if (add && initialMerchant.current === null) initialMerchant.current = JSON.stringify(merchant)
  }, [])

  const hasChanges = useMemo(() => {
    if (initialMerchant.current === null) return false
    return JSON.stringify(merchant) !== initialMerchant.current
  }, [merchant])

  useEffect(() => {
    const service = getTintVTOService()
    ;(async () => {
      const response = await service.plans()
      const plansJson = (await response.json()) as PlanOption[]
      setPlans(plansJson.map((plan) => ({ id: plan.id, name: plan.name })))

      const {
        data: { listUsers },
      } = await service.listUsers()
      setUsers(listUsers)
      setUsersLoading(false)
    })()
  }, [])

  const update = (patch: Partial<MerchantData>) => {
    setMerchant((prev) => ({ ...prev, ...patch }))
  }

  const isValid = () => {
    return !!(
      merchant.name?.trim() &&
      merchant.email?.trim() &&
      merchant.domain?.trim() &&
      merchant.description?.trim() &&
      merchant.planId?.trim() &&
      merchant.brandsIds?.length &&
      merchant.cognitoSub
    )
  }

  const handleSave = () => {
    saveMerchantMutation.mutate(merchant, {
      onSuccess: () => navigate("/merchants"),
    })
  }

  const selectedBrandIds = merchant.brandsIds?.map((item) => item.toString()) ?? []

  const toggleBrand = (brandId: string) => {
    const nextIds = selectedBrandIds.includes(brandId)
      ? selectedBrandIds.filter((id) => id !== brandId)
      : [...selectedBrandIds, brandId]
    update({ brandsIds: nextIds })
  }

  const removeBrand = (brandId: string) => {
    update({ brandsIds: selectedBrandIds.filter((id) => id !== brandId) })
  }

  const filteredBrands = useMemo(() => {
    if (!brands) return []
    const query = brandSearch.toLowerCase().trim()
    const filtered = query
      ? brands.filter((b: BrandOption) => b.name.toLowerCase().includes(query))
      : brands

    // Show selected brands first, then unselected
    return [...filtered].sort((a: BrandOption, b: BrandOption) => {
      const aSelected = selectedBrandIds.includes(a.id) ? 0 : 1
      const bSelected = selectedBrandIds.includes(b.id) ? 0 : 1
      return aSelected - bSelected
    })
  }, [brands, brandSearch, selectedBrandIds])

  return (
    <div data-testid={add ? "merchant-add-page" : "merchant-edit-page"}>
      <FixedHeaderLayout
        title={`${add ? t("merchantPage.add") : t("merchantPage.edit")} ${t("merchantPage.title")}`}
        onBack={() => navigate("/merchants")}
        backLabel={t("backToMerchants")}
        onFirstButtonClicked={() => navigate("/merchants")}
        onSecondButtonClicked={handleSave}
        onSecondButtonLoading={isLoading || saveMerchantMutation.isPending}
        secondButtonDisabled={!hasChanges}
        validateSecondButton={isValid}
      />

      <div className="p-6">
        <div className="grid grid-cols-[1fr_1fr] gap-4">
          {/* Left column: General + Plan */}
          <div className="space-y-4">
            <Card data-testid="merchant-general-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">General information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="name" className="text-xs">
                      {t("name")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="merchant-name-input"
                      value={merchant.name}
                      onChange={(e) => update({ name: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="email" className="text-xs">
                      {t("email")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      data-testid="merchant-email-input"
                      value={merchant.email}
                      onChange={(e) => update({ email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="domain" className="text-xs">
                      {t("domain")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="domain"
                      data-testid="merchant-domain-input"
                      value={merchant.domain}
                      onChange={(e) => update({ domain: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="description" className="text-xs">
                      {t("description")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="description"
                      data-testid="merchant-description-input"
                      value={merchant.description}
                      onChange={(e) => update({ description: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="merchant-plan-owner-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Plan & Owner</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">
                      {t("plan")} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      disabled={!plans.length}
                      value={merchant.planId}
                      onValueChange={(value) => update({ planId: value })}
                    >
                      <SelectTrigger data-testid="merchant-plan-select">
                        <SelectValue placeholder={t("selectPlan")} />
                      </SelectTrigger>
                      <SelectContent data-testid="merchant-plan-options">
                        {plans.map((plan) => (
                          <SelectItem
                            key={plan.id}
                            value={plan.id}
                            data-testid="merchant-plan-option"
                          >
                            {plan.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">
                      {t("user")} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      disabled={isUsersLoading}
                      value={merchant.cognitoSub}
                      onValueChange={(value) => update({ cognitoSub: value })}
                    >
                      <SelectTrigger data-testid="merchant-user-select">
                        <SelectValue placeholder={t("selectUser")} />
                      </SelectTrigger>
                      <SelectContent data-testid="merchant-user-options">
                        {users.map(({ username, sub }) => (
                          <SelectItem key={sub} value={sub} data-testid="merchant-user-option">
                            {username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column: Brands */}
          <Card className="flex flex-col" data-testid="merchant-brands-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">
                  {t("brands")} <span className="text-destructive">*</span>
                </CardTitle>
                {selectedBrandIds.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {selectedBrandIds.length} selected
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-3">
              {/* Selected brands badges */}
              {selectedBrandIds.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="merchant-selected-brands">
                  {selectedBrandIds.map((id) => {
                    const brand = brands?.find((b: BrandOption) => b.id === id)
                    return (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="gap-1 text-xs"
                        data-testid="merchant-selected-brand"
                        data-brand-id={id}
                      >
                        {brand?.name ?? id}
                        <button
                          type="button"
                          data-testid="merchant-selected-brand-remove"
                          className="ml-0.5 rounded-full outline-none hover:bg-muted"
                          onClick={() => removeBrand(id)}
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="merchant-brand-search"
                  placeholder="Search brands..."
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  className="pl-8"
                />
              </div>

              {/* Brand list */}
              <div
                data-testid="merchant-brand-list"
                className="flex-1 overflow-y-auto rounded-md border"
                style={{ maxHeight: "280px" }}
              >
                {isBrandsLoading ? (
                  <p
                    className="py-4 text-center text-sm text-muted-foreground"
                    data-testid="merchant-brand-loading"
                  >
                    {t("loading")}...
                  </p>
                ) : filteredBrands.length === 0 ? (
                  <p
                    className="py-4 text-center text-sm text-muted-foreground"
                    data-testid="merchant-brand-empty"
                  >
                    No brands found
                  </p>
                ) : (
                  <div className="p-1.5">
                    {filteredBrands.map((brand: BrandOption) => (
                      <div
                        key={brand.id}
                        data-testid="merchant-brand-option"
                        data-brand-id={brand.id}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`brand-${brand.id}`}
                          data-testid="merchant-brand-checkbox"
                          checked={selectedBrandIds.includes(brand.id)}
                          onCheckedChange={() => toggleBrand(brand.id)}
                        />
                        <Label
                          htmlFor={`brand-${brand.id}`}
                          className="flex-1 cursor-pointer truncate text-sm font-normal"
                        >
                          {brand.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
